import { MouseEvent, ReactNode, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { CourseMappingDropdownOption } from './courseMappingDropdownOptions';
import CourseMappingSelectTag from './CourseMappingSelectTag';

interface CourseMappingDropdownCellProps {
  columnKey: string;
  displayValue: ReactNode | null;
  otherValuesCount?: number;
  options: CourseMappingDropdownOption[];
  selectedValue: string | null;
  ariaLabel: string;
  onSelect: (value: string) => void;
}

const CourseMappingDropdownCell = ({
  columnKey,
  displayValue,
  otherValuesCount = 0,
  options,
  selectedValue,
  ariaLabel,
  onSelect,
}: CourseMappingDropdownCellProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (value: string) => {
    onSelect(value);
    handleClose();
  };

  return (
    <>
      <button
        type="button"
        className={`course-mapping-dropdown-cell${isOpen ? ' course-mapping-dropdown-cell--focused' : ''}`}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={handleOpen}
      >
        <span className="course-mapping-dropdown-cell-content">
          {displayValue ? (
            <span className="course-mapping-dropdown-cell-value">{displayValue}</span>
          ) : (
            <span className="course-mapping-dropdown-cell-placeholder" aria-hidden="true" />
          )}
        </span>
        <span className="course-mapping-dropdown-cell-trailing">
          {otherValuesCount > 0 ? (
            <span
              className="course-mapping-smart-cell-indicator"
              aria-label={`${otherValuesCount} other ${otherValuesCount === 1 ? 'value' : 'values'}`}
            >
              +{otherValuesCount}
            </span>
          ) : null}
          <ArrowDropDownIcon className="course-mapping-dropdown-cell-icon" fontSize="small" />
        </span>
      </button>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        className="course-mapping-dropdown-menu"
        PaperProps={{
          className: 'course-mapping-dropdown-menu-paper',
        }}
      >
        {options.map((option) => {
          const isSelected = option.value === selectedValue;

          return (
            <MenuItem
              key={option.value}
              className={`course-mapping-dropdown-menu-item${
                isSelected ? ' course-mapping-dropdown-menu-item--selected' : ''
              }`}
              aria-selected={isSelected}
              onClick={() => handleSelect(option.value)}
            >
              <span className="course-mapping-dropdown-menu-item-content">
                <CourseMappingSelectTag label={option.label} columnKey={columnKey} />
              </span>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default CourseMappingDropdownCell;
